import { List, Plus, PlusCircle } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Image } from '@unistocks/components/basic/image';
import { ItemSeparatorProps, ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { FabMenu, FabMenuGroup } from '@unistocks/components/floating-action-button-menu/fab-menu';
import { EmptyList } from '@unistocks/components/no-data/empty-list';
import { Page } from '@unistocks/components/page/page';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { AssetSettingsProvider } from '@unistocks/features/portfolios/components/asset-settings/asset-settings-provider';
import { AddOrUpdateSectionForm } from '@unistocks/features/portfolios/components/forms/add-or-update-section-form';
import {
  getPortfolioTableItemType,
  keyExtractor,
  renderPortfolioTableItem,
  usePortfolioTableData
} from '@unistocks/features/portfolios/components/portfolio-table/portfolio-table';
import { PortfolioTableItem } from '@unistocks/features/portfolios/components/portfolio-table/types';
import { PortfolioAddButtonEvent } from '@unistocks/features/portfolios/models/portfolio-menu';
import { WatchlistHeaderRight } from '@unistocks/features/watchlists/components/header/header-right';
import { WatchlistHeaderTitle } from '@unistocks/features/watchlists/components/header/header-title';
import { invalidateWatchlistQueriesById } from '@unistocks/features/watchlists/watchlist-utils';
import {
  useWatchlistsCreateSectionMutation,
  useWatchlistsGetQuery,
  watchlistsGetQueryKey
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import { UNISTOCKS_ARROW } from '@unistocks/shared/consts';
import { t } from '@unistocks/shared/localization/index.ts';
import { HasId } from '@unistocks/types/has-id';
import { useRouter } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import { Circle, isWeb, Separator } from 'tamagui';

export const Watchlist: React.FC<HasId> = ({ id }) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: watchlist, isSuccess, isError, isRefetching } = useWatchlistsGetQuery({ id });
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const title = isSuccess
    ? watchlist.name
    : isError && !isWeb
      ? t('::SelectWhat', { 0: t('::Watchlist').toLowerCase() })
      : null;

  const onRefresh = useCallback(() => {
    invalidateWatchlistQueriesById(queryClient, id);
  }, [id, queryClient]);

  const tableData = usePortfolioTableData(watchlist);

  const { mutateAsync: addSection } = useWatchlistsCreateSectionMutation(id, {
    onMutate: () => {
      setSectionModalOpen(false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: watchlistsGetQueryKey(id) });
    }
  });

  const fabMenuItems: FabMenuGroup<PortfolioAddButtonEvent>[] = useMemo(() => {
    if (!watchlist) {
      return [];
    }

    const sectionItems: FabMenuGroup = {
      key: 1,
      groupHeader: `${t('::AddWhat', { 0: t('::Holdings').toLocaleLowerCase() })} ${t(
        '::AddSuffix'
      )}`,
      value: watchlist.sections.map(section => ({
        key: section.id,
        title: section.name,
        icon: (
          <Circle bg={'$color5'}>
            <Plus color={'$success'} />
          </Circle>
        ),
        value: {
          type: 'asset',
          params: { sectionId: section.id }
        }
      }))
    };

    return [
      sectionItems,
      {
        key: 2,
        value: [
          {
            key: 'category',
            title: t('::AddWhat', { 0: t('::Section').toLocaleLowerCase() }),
            icon: ({ size }) => (
              <Image
                source={UNISTOCKS_ARROW}
                style={{ width: size, height: size, overflow: 'visible' }}
              />
            ),
            value: { type: 'category' }
          }
        ]
      }
    ];
  }, [watchlist]);

  const onMenuItemPress = useCallback(
    (item: PortfolioAddButtonEvent) => {
      switch (item.type) {
        case 'asset':
          router.push({
            pathname: '/(tabs)/watchlists/[id]/add-asset',
            params: { id, sectionId: item.params?.sectionId }
          });
          break;

        case 'category':
          setSectionModalOpen(true);
          break;
      }
    },
    [id, router]
  );

  return (
    <AssetSettingsProvider portfolio={watchlist}>
      <Page
        queryState={{
          queryKeys: [watchlistsGetQueryKey(id)],
          loadingText: t('::Watchlist:Loading')
        }}
      >
        <Page.VirtualList
          data={tableData}
          extraData={watchlist}
          keyExtractor={keyExtractor}
          getItemType={getPortfolioTableItemType}
          renderItem={renderPortfolioTableItem}
          ItemSeparatorComponent={RowSeparator}
          ListEmptyComponent={
            isSuccess ? (
              <EmptyList
                loadingText={t('::Watchlist:Loading')}
                noDataText={t('::Watchlist:PortfolioTable:NoData')}
                noDataIcon={List}
              />
            ) : null
          }
          refreshing={isRefetching}
          onRefresh={onRefresh}
          contentContainerStyle={ScrollViewStyles.contentContainerStyle}
          headerTitle={<WatchlistHeaderTitle>{title}</WatchlistHeaderTitle>}
          headerIcon={isSuccess ? <WatchlistHeaderRight id={id} /> : undefined}
          headerBackArrow={false}
        />

        <FabMenu onItemPress={onMenuItemPress} items={fabMenuItems} />

        <Sheet
          open={sectionModalOpen}
          onOpenChange={setSectionModalOpen}
          headerTitle={t('::AddWhat', { 0: t('::Category').toLowerCase() })}
          headerIcon={<PlusCircle color={'$success'} />}
          index={1}
        >
          {watchlist ? (
            <AddOrUpdateSectionForm portfolio={watchlist} onSubmit={addSection} />
          ) : null}
        </Sheet>
      </Page>
    </AssetSettingsProvider>
  );
};

Watchlist.displayName = 'Watchlist';

const RowSeparator: React.FC<ItemSeparatorProps<PortfolioTableItem>> = ({ leadingItem }) => {
  return leadingItem.type === 'sectionFooter' ? null : <Separator />;
};

RowSeparator.displayName = 'RowSeparator';
